import {
  AIRTIME_MANAGEMENT,
  CARRIER_MANAGEMENT,
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  STORE_API,
} from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseCategoryRequestModel } from "app/based/baseApiModel/baseCategory/BaseCategoryRequestModel";
import { BaseCategoryResponseModel } from "app/based/baseApiModel/baseCategory/BaseCategoryResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { NiceSimRegistrationResponseModel } from "features/niceSimRegistration/data/model/NiceSimRegistrationResponseModel";
import { NiceSimRegistrationInjection } from "features/niceSimRegistration/di/NiceSimRegistrationInjection";
import { ExportPayload } from "features/niceSimRegistration/domain/entities/NiceSimRegistrationPayload";
import {
  NiceSimRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "features/niceSimRegistration/domain/entities/NiceSimRegistrationQuery";
import { NiceSimRegistrationRequestEntity } from "features/niceSimRegistration/domain/entities/NiceSimRegistrationRequestEntity";
import { INiceSimRegistrationRepository } from "features/niceSimRegistration/domain/repositories/INiceSimRegistrationRepository";
import { isEmpty } from "lodash";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";

@injectable()
export class NiceSimRegistrationUseCase {
  private niceSimRegistrationRepo: INiceSimRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.niceSimRegistrationRepo =
      NiceSimRegistrationInjection.getNiceSimRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  public shouldUsePrivateTransactionList(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionList =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransList
      );

    return hasPrivateTransactionList === true;
  }

  public shouldUsePrivateTransactionDetail(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionDetail =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransDetail
      );

    return hasPrivateTransactionDetail === true;
  }

  private getListFromRepository(params: NiceSimRegistrationRequestEntity) {
    return this.niceSimRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
      platform: paramsChecking(rawFormValues?.platform, "select") as string,
      partner: paramsChecking(rawFormValues?.partner, "select") as string,
      user: (rawFormValues?.user as string[]) || undefined,
      store: paramsChecking(rawFormValues?.store, "select") as string,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from"
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to"
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getList(
    params: NiceSimRegistrationRequestEntity
  ): Promise<ListBaseApiResponseModel<NiceSimRegistrationResponseModel>> {
    return await this.getListFromRepository(params);
  }

  async getNiceSimRegistrationListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext
  ): Promise<{
    list: ListBaseApiResponseModel<NiceSimRegistrationResponseModel>;
    params: NiceSimRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = NiceSimRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getNiceSimRegistrationListWithTableChange(
    prevParams: NiceSimRegistrationRequestEntity,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number
  ): Promise<{
    list: ListBaseApiResponseModel<NiceSimRegistrationResponseModel>;
    params: NiceSimRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = NiceSimRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getNiceSimRegistrationListRedo(): Promise<{
    list: ListBaseApiResponseModel<NiceSimRegistrationResponseModel>;
    params: NiceSimRegistrationRequestEntity;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = NiceSimRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? ""
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getDetail(
    params: NiceSimRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<NiceSimRegistrationResponseModel>> {
    return await this.niceSimRegistrationRepo.getDetail(
      params,
      this.shouldUsePrivateTransactionDetail()
    );
  }

  async exportReport(
    params: NiceSimRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.niceSimRegistrationRepo.exportReport(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async exportNiceSimRegistrationReportFromSearch(
    payload: ExportPayload
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = NiceSimRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo
    );
    return await this.exportReport(params);
  }

  async checkStatus(
    code: string
  ): Promise<BaseApiResponseModel<NiceSimRegistrationResponseModel>> {
    return await this.niceSimRegistrationRepo.checkStatus(code);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination" | "package"
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = CARRIER_MANAGEMENT.LIST;
        break;
      case "partner":
        path = PARTNER_MANAGEMENT.LIST;
        break;
      case "store":
        path = STORE_API.LIST;
        break;
      case "denomination":
        path = AIRTIME_MANAGEMENT.LIST;
        break;
      case "package":
        path = FILTER_FEATURE.PACKAGE_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getNiceSimCategory(
    params: BaseCategoryRequestModel,
    category: "zodiac" | "numerology" | "destiny" | "numberType"
  ): Promise<Array<BaseCategoryResponseModel>> {
    let path = "";
    let savedKey = "";
    switch (category) {
      case "zodiac":
        path = FILTER_FEATURE.ZODIAC_LIST;
        savedKey = CacheKeyEnum.zodiac;
        break;
      case "numerology":
        path = FILTER_FEATURE.NUMEROLOGY_LIST;
        savedKey = CacheKeyEnum.numerology;
        break;
      case "destiny":
        path = FILTER_FEATURE.DESTINY_LIST;
        savedKey = CacheKeyEnum.destiny;
        break;
      case "numberType":
        path = FILTER_FEATURE.NUMER_TYPE_LIST;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
      savedKey: !isEmpty(savedKey) && savedKey,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B"
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    let capabilityKey = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        capabilityKey = CapabilityEnum.staffFilter;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      capabilityKey: !isEmpty(capabilityKey) && capabilityKey,
    });
  }
}
